import { Badge, Button, Card, Group, Progress, Stack, Text, Tooltip } from '@mantine/core';
import { IconAlertCircle, IconDownload } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { openImportErrorDetailModal } from '@/pages/NewAdminConsole/components/importErrorDetailModal';
import type { UserImportTask } from '@/pages/NewAdminConsole/services/permissionApi';

interface Props {
  task: UserImportTask;
  compact?: boolean;
}

const statusColor = (task: UserImportTask): string => {
  if (task.status === 'READY') return 'gray';
  if (task.status === 'PROCESSING') return 'blue';
  if (task.failedAmount > 0) return 'orange';
  return 'green';
};

export const ImportTaskCard = ({ task, compact }: Props) => {
  const { t } = useTranslation();

  const percent = task.totalAmount > 0 ? Math.round((task.finishedAmount / task.totalAmount) * 100) : 0;

  const showErrorsButton = task.status === 'FINISHED' && task.failedAmount > 0;

  return (
    <Card withBorder radius="md" p="sm">
      <Stack gap="xs">
        <Group justify="space-between" wrap="nowrap" align="flex-start">
          <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
            <Tooltip label={task.fileName} withArrow openDelay={400}>
              <Text fw={500} size="sm" truncate>
                {task.fileName}
              </Text>
            </Tooltip>
            <Text size="xs" c="dimmed">
              {t('adminConsole.newConsole.import.tasks.createdAt')}: {dayjs(task.createdAt).format('YYYY-MM-DD HH:mm')}
            </Text>
          </Stack>
          <Badge color={statusColor(task)} variant="light">
            {t(`adminConsole.newConsole.import.tasks.status.${task.status}`)}
          </Badge>
        </Group>

        <Progress
          value={percent}
          color={statusColor(task)}
          size="md"
          radius="xl"
          striped={task.status === 'PROCESSING'}
          animated={task.status === 'PROCESSING'}
        />

        <Group justify="space-between" wrap="wrap" gap="xs">
          <Text size="xs" c="dimmed">
            {t('adminConsole.newConsole.import.tasks.progressLabel', { percent })}
          </Text>
          <Text size="xs" c="dimmed">
            {t('adminConsole.newConsole.import.tasks.summary', {
              total: task.totalAmount,
              success: task.succeededAmount,
              fail: task.failedAmount,
            })}
          </Text>
        </Group>

        {showErrorsButton && (
          <Group justify="flex-end">
            <Button
              size="xs"
              variant="light"
              color="orange"
              leftSection={<IconAlertCircle size={14} />}
              rightSection={<IconDownload size={14} />}
              onClick={() => openImportErrorDetailModal(task.id, t, { compact })}
            >
              {t('adminConsole.newConsole.import.tasks.viewErrors')}
            </Button>
          </Group>
        )}
      </Stack>
    </Card>
  );
};
